import React from "react";
import PropTypes from "prop-types";
import { withStyles } from "@material-ui/core/styles";
import {
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TablePagination,
  Chip,
  Box,
  CircularProgress,
  Divider
} from "@material-ui/core";
import TrendingUpIcon from "@material-ui/icons/TrendingUp";
import utils from "../../universal/utils";
import MotorcycleIcon from "@material-ui/icons/Motorcycle";
import TodayIcon from "@material-ui/icons/Today";
import DateRangeIcon from "@material-ui/icons/DateRange";
import CalendarTodayIcon from "@material-ui/icons/CalendarToday";
import PeopleIcon from "@material-ui/icons/People";

const styles = (theme) => ({
  root: {
    padding: theme.spacing(3),
  },
  statsContainer: {
    marginBottom: theme.spacing(3),
  },
  statCard: {
    height: "100%",
    textAlign: "center",
    background: "linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)",
    color: "white",
  },
  statCardAlt: {
    height: "100%",
    textAlign: "center",
    background: "linear-gradient(45deg, #FF6B6B 30%, #4ECDC4 90%)",
    color: "white",
  },
  statCardSuccess: {
    height: "100%",
    textAlign: "center",
    background: "linear-gradient(45deg, #4CAF50 30%, #8BC34A 90%)",
    color: "white",
  },
  statCardWarning: {
    height: "100%",
    textAlign: "center",
    background: "linear-gradient(45deg, #FF9800 30%, #FFC107 90%)",
    color: "white",
  },
  statNumber: {
    fontSize: "2.5rem",
    fontWeight: "bold",
    marginBottom: theme.spacing(1),
  },
  statLabel: {
    fontSize: "1rem",
    opacity: 0.9,
  },
  statIcon: {
    fontSize: "3rem",
    marginBottom: theme.spacing(1),
    opacity: 0.8,
  },
  tableContainer: {
    marginTop: theme.spacing(3),
  },
  tableHeader: {
    backgroundColor: theme.palette.grey[100],
    fontWeight: "bold",
  },
  motorcycleModelSizesContainer: {
    display: "flex",
    flexDirection: "column",
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: 200,
  },
  topMotorcycleChip: {
    margin: theme.spacing(0.5),
    backgroundColor: theme.palette.primary.light,
    color: "white",
  },
  sectionTitle: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(2),
    color: theme.palette.primary.main,
    fontWeight: "bold",
  },
  motorcycleInfo: {
    fontSize: "0.875rem",
    color: theme.palette.text.secondary,
  },
  usageTypeChip: {
    fontSize: "0.75rem",
  },
});

const basePath = utils.getEdgeBasePath();

const hebrewLabels = {
  title: "ניתוח שימוש באשף בחירת צמיגים",
  stats: {
    daily: "שימושים היום",
    weekly: "שימושים השבוע",
    monthly: "שימושים החודש",
    total: "סה\"כ שימושים",
  },
  topMotorcycles: "אופנועים פופולריים",
  usageTable: "רשימת שימושים באשף",
  tableHeaders: {
    date: "תאריך",
    visitorId: "מזהה משתמש",
    visitorName: "שם הלקוח",
    visitorPhone: "טלפון הלקוח",
    motorcycle: "אופנוע",
    usage: "סוג שימוש",
    kilometers: "קילומטרים בחודש",
    tireType: "סוג צמיג",
    selectedProduct: "המוצר שנבחר",
  },
  noData: "אין נתונים להצגה",
  loading: "טוען נתונים...",
};

class WizardAnalytics extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      stats: null,
      usages: [],
      pagination: {
        currentPage: 1,
        totalPages: 1,
        totalItems: 0,
        itemsPerPage: 25,
      },
      loading: true,
      usagesLoading: false,
      sortBy: "createdAt",
      sortOrder: "desc",
    };
  }

  componentDidMount() {
    this.loadStats();
    this.loadUsages();
  }

  loadStats = async () => {
    try {
      const response = await fetch(`${basePath}/api/v1/admin/wizard/stats`);
      const data = await response.json();
      
      if (data.ok) {
        this.setState({ stats: data.stats });
      }
    } catch (error) {
      console.error("Error loading wizard stats:", error);
    }
  };

  loadUsages = async (page = 1, sortBy = "createdAt", sortOrder = "desc") => {
    this.setState({ usagesLoading: true });
    
    try {
      const { itemsPerPage } = this.state.pagination;
      const response = await fetch(
        `${basePath}/api/v1/admin/wizard/usages?page=${page}&limit=${itemsPerPage}&sortBy=${sortBy}&sortOrder=${sortOrder}`
      );
      const data = await response.json();
      
      if (data.ok) {
        this.setState({
          usages: data.usages,
          pagination: data.pagination,
          sortBy,
          sortOrder,
        });
      }
    } catch (error) {
      console.error("Error loading wizard usages:", error);
    } finally {
      this.setState({ usagesLoading: false, loading: false });
    }
  };

  handleSort = (field) => {
    const { sortBy, sortOrder } = this.state;
    const newSortOrder = 
      sortBy === field && sortOrder === "desc" ? "asc" : "desc";
    
    this.loadUsages(1, field, newSortOrder);
  };

  handlePageChange = (event, newPage) => {
    const { sortBy, sortOrder } = this.state;
    this.loadUsages(newPage + 1, sortBy, sortOrder);
  };

  handleRowsPerPageChange = (event) => {
    const newItemsPerPage = parseInt(event.target.value, 10);
    this.setState(prevState => ({
      pagination: {
        ...prevState.pagination,
        itemsPerPage: newItemsPerPage,
      }
    }));
    this.loadUsages(1, this.state.sortBy, this.state.sortOrder);
  };

  formatMotorcycleDisplay = (usage) => {
    // First check if we have enriched motorcycle details
    if (usage.motorcycleDetails && usage.motorcycleDetails.model) {
      return usage.motorcycleDetails.model;
    }
    // Fall back to the motorcycle ID string
    if (usage.wizardResponses.motorcycle) {
      return usage.wizardResponses.motorcycle;
    }
    return "לא זוהה";
  };

  getUsageTypeColor = (usage) => {
    const usageMap = {
      "city": "primary",
      "highway": "secondary", 
      "mixed": "default",
      "track": "error",
      "offroad": "warning",
    };
    return usageMap[usage] || "default";
  };

  getTireTypeColor = (type) => {
    const typeMap = {
      "front": "primary",
      "rear": "secondary",
      "both": "default",
    };
    return typeMap[type] || "default";
  };

  formatSelectedProduct = (usage) => {
    if (usage.clickedTires && usage.clickedTires.length > 0) {
      const clickedTire = usage.clickedTires[usage.clickedTires.length - 1]; 
      if (clickedTire.brand && clickedTire.name && clickedTire.displaySize) {
        return `${clickedTire.brand} ${clickedTire.name} ${clickedTire.displaySize}`;
      }
    }
    
    return "לא נבחר מוצר";
  };

  formatMotorcycleSizes = (motorcycleDetails) => {
    if (
      motorcycleDetails &&
      Array.isArray(motorcycleDetails.yearRanges) &&
      motorcycleDetails.yearRanges.length > 0
    ) {
      const { front, rear } = motorcycleDetails.yearRanges[0];
      if (front && rear) {
        const format = (tire) =>
          `${tire.width}/${tire.profile}ZR${tire.rim}`;
        return `${format(rear)} + ${format(front)}`;
      }
    }
    return null;
  };

  renderStatsCards = () => {
    const { classes } = this.props;
    const { stats } = this.state;

    if (!stats) return null;

    return (
      <Grid container spacing={3} className={classes.statsContainer}>
        <Grid item xs={12} sm={6} md={3}>
          <Card className={classes.statCard}>
            <CardContent>
              <TodayIcon className={classes.statIcon} />
              <Typography className={classes.statNumber}>
                {stats.dailyCount}
              </Typography>
              <Typography className={classes.statLabel}>
                {hebrewLabels.stats.daily}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card className={classes.statCardSuccess}>
            <CardContent>
              <DateRangeIcon className={classes.statIcon} />
              <Typography className={classes.statNumber}>
                {stats.weeklyCount}
              </Typography>
              <Typography className={classes.statLabel}>
                {hebrewLabels.stats.weekly}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card className={classes.statCardWarning}>
            <CardContent>
              <CalendarTodayIcon className={classes.statIcon} />
              <Typography className={classes.statNumber}>
                {stats.monthlyCount}
              </Typography>
              <Typography className={classes.statLabel}>
                {hebrewLabels.stats.monthly}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card className={classes.statCardAlt}>
            <CardContent>
              <PeopleIcon className={classes.statIcon} />
              <Typography className={classes.statNumber}>
                {stats.totalCount}
              </Typography>
              <Typography className={classes.statLabel}>
                {hebrewLabels.stats.total}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    );
  };

  renderTopMotorcycles = () => {
    const { classes } = this.props;
    const { stats } = this.state;

    if (!stats || !stats.topMotorcycles.length) return null;

    return (
      <Paper className={classes.tableContainer} elevation={2}>
        <Box p={2}>
          <Typography variant="h6" className={classes.sectionTitle}>
            <MotorcycleIcon style={{ marginRight: 8, verticalAlign: "middle" }} />
            {hebrewLabels.topMotorcycles}
          </Typography>
          <Divider style={{ margin: "16px 0" }} />
          <Box>
            {stats.topMotorcycles.slice(0, 10).map((motorcycle, index) => (
              <Chip
                key={index}
                label={`${motorcycle.modelName || motorcycle.motorcycleId || "לא זוהה"} (${motorcycle.count})`}
                className={classes.topMotorcycleChip}
                variant="outlined"
              />
            ))}
          </Box>
        </Box>
      </Paper>
    );
  };

  renderUsagesTable = () => {
    const { classes } = this.props;
    const { usages, pagination, loading, usagesLoading, sortBy, sortOrder } = this.state;

    console.log(usages);

    if (loading) {
      return (
        <div className={classes.loadingContainer}>
          <CircularProgress />
          <Typography variant="body1" style={{ marginLeft: 16 }}>
            {hebrewLabels.loading}
          </Typography>
        </div>
      );
    }

    return (
      <Paper className={classes.tableContainer} elevation={2}>
        <Box p={2}>
          <Typography variant="h6" className={classes.sectionTitle}>
            <TrendingUpIcon style={{ marginRight: 8, verticalAlign: "middle" }} />
            {hebrewLabels.usageTable}
          </Typography>
        </Box>
        
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell className={classes.tableHeader}>
                  <TableSortLabel
                    active={sortBy === "createdAt"}
                    direction={sortBy === "createdAt" ? sortOrder : "asc"}
                    onClick={() => this.handleSort("createdAt")}
                  >
                    {hebrewLabels.tableHeaders.date}
                  </TableSortLabel>
                </TableCell>
                <TableCell className={classes.tableHeader}>
                  {hebrewLabels.tableHeaders.visitorId}
                </TableCell>
                <TableCell className={classes.tableHeader}>
                  {hebrewLabels.tableHeaders.visitorName}
                </TableCell>
                <TableCell className={classes.tableHeader}>
                  {hebrewLabels.tableHeaders.visitorPhone}
                </TableCell>
                <TableCell className={classes.tableHeader}>
                  {hebrewLabels.tableHeaders.motorcycle}
                </TableCell>
                <TableCell className={classes.tableHeader}>
                  {hebrewLabels.tableHeaders.usage}
                </TableCell>
                <TableCell className={classes.tableHeader}>
                  {hebrewLabels.tableHeaders.kilometers}
                </TableCell>
                <TableCell className={classes.tableHeader}>
                  {hebrewLabels.tableHeaders.tireType}
                </TableCell>
                <TableCell className={classes.tableHeader}>
                  {hebrewLabels.tableHeaders.selectedProduct}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {usagesLoading ? (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              ) : usages.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    {hebrewLabels.noData}
                  </TableCell>
                </TableRow>
              ) : (
                usages.map((usage) => (
                  <TableRow key={usage._id}>
                    <TableCell>
                      {usage.formattedDate}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" style={{ fontFamily: "monospace" }}>
                        {usage.visitorId ? usage.visitorId.substring(0, 8) + "..." : "אנונימי"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {usage.wizardResponses.visitorName || "לא צוין"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {usage.wizardResponses.visitorPhoneNumber || "לא צוין"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2">
                          {this.formatMotorcycleDisplay(usage)}
                        </Typography>
                        {usage.motorcycleDetails && (
                          <>
                            <div className={classes.motorcycleModelSizesContainer}>
                              <Typography variant="caption" className={classes.motorcycleInfo}>
                                ID: {usage.motorcycleDetails.id}
                              </Typography>
                              {this.formatMotorcycleSizes(usage.motorcycleDetails) && (
                                <Typography variant="caption" className={classes.motorcycleInfo}>
                                  {this.formatMotorcycleSizes(usage.motorcycleDetails)}
                                </Typography>
                              )}
                            </div>
                          </>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={usage.wizardResponses.usage || "לא צוין"}
                        color={this.getUsageTypeColor(usage.wizardResponses.usage)}
                        className={classes.usageTypeChip}
                      />
                    </TableCell>
                    <TableCell>
                      {usage.wizardResponses.kilometers || "לא צוין"}
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={usage.wizardResponses.selectedTyreType || "לא צוין"}
                        color={this.getTireTypeColor(usage.wizardResponses.selectedTyreType)}
                        className={classes.usageTypeChip}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {this.formatSelectedProduct(usage)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        
        <TablePagination
          component="div"
          count={pagination.totalItems}
          page={pagination.currentPage - 1}
          onPageChange={this.handlePageChange}
          rowsPerPage={pagination.itemsPerPage}
          onRowsPerPageChange={this.handleRowsPerPageChange}
          rowsPerPageOptions={[10, 25, 50, 100]}
          labelRowsPerPage="שורות בעמוד:"
          labelDisplayedRows={({ from, to, count }) => 
            `${from}-${to} מתוך ${count}`
          }
        />
      </Paper>
    );
  };

  render() {
    const { classes } = this.props;

    return (
      <div className={classes.root}>
        {this.renderStatsCards()}
        {this.renderTopMotorcycles()}
        {this.renderUsagesTable()}
      </div>
    );
  }
}

WizardAnalytics.propTypes = {
  classes: PropTypes.object.isRequired,
};

export default withStyles(styles)(WizardAnalytics); 